import { Ban, Check, Route, type LucideIcon } from 'lucide-react';
import { Label, RadioGroup, Textarea } from '@bliks-io/ui';
import { type VariantCheckAnswer } from '@bliks-io/contracts';
import {
  RadioCard,
  RadioCardDescription,
  RadioCardHeader,
  RadioCardIcon,
  RadioCardItem,
  RadioCardLabel,
  RadioCardText
} from '@/components/form/RadioCard';

/** The Variant Check answers, in reading order (ADR 0022). */
const VARIANT_ANSWERS: Array<{
  answer: VariantCheckAnswer;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
}> = [
  {
    answer: 'same',
    labelKey: 'surveyDialog.variantSame',
    descriptionKey: 'surveyDialog.variantSameDescription',
    icon: Check
  },
  {
    answer: 'different',
    labelKey: 'surveyDialog.variantDifferent',
    descriptionKey: 'surveyDialog.variantDifferentDescription',
    icon: Route
  },
  {
    answer: 'not_performed',
    labelKey: 'surveyDialog.variantNotPerformed',
    descriptionKey: 'surveyDialog.variantNotPerformedDescription',
    icon: Ban
  }
];

interface VariantVerdictSectionProps {
  value: VariantCheckAnswer | undefined;
  onChange: (answer: VariantCheckAnswer) => void;
  /** The chosen answer stays visible, and unchangeable, while it is in flight. */
  disabled?: boolean;
  t: (key: string) => string;
}

/** First page of the survey: "Machst du das genau so?" */
export function VariantVerdictSection({
  value,
  onChange,
  disabled,
  t
}: VariantVerdictSectionProps) {
  return (
    <div className="space-y-4">
      <Label className="text-base font-semibold">{t('surveyDialog.variantQuestion')}</Label>
      <RadioGroup
        value={value ?? ''}
        onValueChange={(answer) => onChange(answer as VariantCheckAnswer)}
        disabled={disabled}
        className="flex flex-col gap-3"
      >
        {VARIANT_ANSWERS.map(({ answer, labelKey, descriptionKey, icon }) => (
          <RadioCard key={answer} value={answer} isActive={value === answer}>
            <RadioCardHeader>
              <RadioCardItem value={answer} />
              <RadioCardIcon icon={icon} />
              <RadioCardText>
                <RadioCardLabel>{t(labelKey)}</RadioCardLabel>
                <RadioCardDescription>{t(descriptionKey)}</RadioCardDescription>
              </RadioCardText>
            </RadioCardHeader>
          </RadioCard>
        ))}
      </RadioGroup>
    </div>
  );
}

interface VariantDescriptionSectionProps {
  value: string;
  onChange: (description: string) => void;
  /** Text typed after the save was fired would never reach the server. */
  disabled?: boolean;
  t: (key: string) => string;
}

/**
 * The text "Ja, aber anders" owes. It travels with the answer on the Variant
 * Check's own endpoint (plan 029), so it is plain local state rather than a
 * field of the classic survey request.
 */
export function VariantDescriptionSection({
  value,
  onChange,
  disabled,
  t
}: VariantDescriptionSectionProps) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
      <Label htmlFor="variantDescription" className="text-base font-semibold">
        {t('surveyDialog.variantDescriptionQuestion')}
        <span className="text-danger">*</span>
      </Label>
      <Textarea
        id="variantDescription"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('surveyDialog.variantDescriptionPlaceholder')}
        disabled={disabled}
        rows={6}
        autoFocus
      />
    </div>
  );
}
